import type { z } from 'zod';

import type { AssignedAgency } from '../../../../auth/agencyAccess';
import type { agencyBrandSchema } from '../../../../supply/agency/model';
import type { Brand } from '../../model';

export type AgencyBrandAssignment = z.infer<typeof agencyBrandSchema>;

type AgencyBrandsFilter = {
  name: string;
  industries: readonly string[];
  page: number;
  size: number;
};

export function buildAgencyByBrandId(
  assignments: readonly AgencyBrandAssignment[],
): ReadonlyMap<string, AssignedAgency> {
  return new Map(
    assignments.flatMap((item) =>
      item.agencyId && item.agencyName
        ? [[item.brandId, { id: item.agencyId, name: item.agencyName }] as const]
        : [],
    ),
  );
}

/** Client-side name/industry filter and paging for an agency's assigned brands. */
export function pageAgencyBrands(
  assignments: readonly AgencyBrandAssignment[],
  filter: AgencyBrandsFilter,
): { content: Brand[]; totalElements: number } {
  const name = filter.name.trim().toLowerCase();
  const selectedIndustries = new Set(filter.industries);
  const brands = assignments
    .filter(
      (item) =>
        item.name.toLowerCase().includes(name) &&
        (!selectedIndustries.size || selectedIndustries.has(item.industry ?? '')),
    )
    .map((item) => ({ ...item, id: item.brandId, countryCodes: [] }));
  return {
    content: brands.slice(filter.page * filter.size, (filter.page + 1) * filter.size),
    totalElements: brands.length,
  };
}

/** Agency preset in the brand editor: the page's agency, else the edited brand's assignment. */
export function resolveBrandEditorAgency(
  brand: Brand | null,
  parentAgency: AssignedAgency | undefined,
  scopeAgency: AssignedAgency | undefined,
  agencyByBrandId: ReadonlyMap<string, AssignedAgency>,
): AssignedAgency | undefined {
  return parentAgency ?? scopeAgency ?? (brand ? agencyByBrandId.get(brand.id) : undefined);
}
